import React, { useContext, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { Clipboard } from "react-feather";

import {
  ContractPlan,
  ContractPlanStatus,
} from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/contracts_pb";

import {
  contractBillingFreqToString,
  contractPlanStatusToString,
  contractPlanTypeToString,
} from "../../../../constants/contracts";
import mainStyles from "../../../../styles";
import ContractPlanComponentsRecord from "./org-contract-plan-components";
import { SnackbarContext } from "context/snackbar-context";

interface ContractPlanRecordProps {
  contractPlans: ContractPlan[];
  contractsTabColSpan: number;
  isExpanded: boolean;
  priceOverrides: Map<number, number>;
}

const ContractPlanRecord = ({
  contractPlans,
  contractsTabColSpan,
  isExpanded,
  priceOverrides,
}: ContractPlanRecordProps) => {
  const theme = useTheme();
  const classes = mainStyles();
  const [isPlanExpanded, setIsPlanExpanded] = useState(false);
  const { copyToClipboard } = useContext(SnackbarContext);
  const contractPlanStatusChipStyleMap = {
    [ContractPlanStatus.PLAN_STATUS_UNSPECIFIED]: classes.neutral,
    [ContractPlanStatus.PLAN_STATUS_ACTIVE]: classes.greenDay,
    [ContractPlanStatus.PLAN_STATUS_CANCELED]: classes.redWine,
    [ContractPlanStatus.PLAN_STATUS_PAST_DUE]: classes.yellowSubmarine,
    [ContractPlanStatus.PLAN_STATUS_TRIALING]: classes.blueMoon,
    [ContractPlanStatus.PLAN_STATUS_TRIAL_ENDED]: classes.blueMoon,
  };

  const displayContractPlanHeadings = () => {
    const headings: Array<{ title: string; key: string }> = [
      { title: "", key: "contract-plan-heading-expand" },
      { title: "ID", key: "contract-plan-heading-id" },
      { title: "Status", key: "contract-plan-heading-status" },
      { title: "Type", key: "contract-plan-heading-type" },
      {
        title: "Billing Frequency",
        key: "contract-plan-heading-billing-frequency",
      },
      { title: "Coupon Codes", key: "contract-plan-heading-couponCodes" },
      { title: "Currency", key: "contract-plan-heading-currency" },
    ];

    return headings.map(({ title, key }) => (
      <TableCell key={key}>
        <b>{title}</b>
      </TableCell>
    ));
  };

  const populateContractPlan = () => {
    if (contractPlans.length == 0) {
      return (
        <TableRow>
          <TableCell
            colSpan={contractsTabColSpan}
            data-testid={"contract-plan-no-results"}
          >
            <b>{"No plans found."}</b>
          </TableCell>
        </TableRow>
      );
    }

    return contractPlans.map((contractPlan: ContractPlan) => (
      <>{displayPlanRecord({ contractPlan })}</>
    ));
  };

  const displayPlanRecord = ({
    contractPlan,
  }: {
    contractPlan: ContractPlan;
  }) => {
    const {
      id,
      status,
      planType,
      billingFrequency,
      couponCodes,
      components,
      currency,
    } = contractPlan;
    return (
      <>
        <TableRow key={id} data-testid={`contract-plan-row-${id}`}>
          <TableCell
            padding="checkbox"
            data-testid={`contract-plan-expand-row-${id}`}
          >
            <IconButton
              aria-label={"contract-plan-expand-row-button"}
              data-testid={"contract-plan-expand-row-button"}
              size="small"
              onClick={() => setIsPlanExpanded(!isPlanExpanded)}
            >
              {isPlanExpanded ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
          </TableCell>
          <TableCell data-testid={"contract-plan-id"}>
            <Typography
              variant="body2"
              align="left"
              sx={classes.clickable}
              onClick={() => copyToClipboard(id)}
            >
              {id}{" "}
              <Clipboard
                color={theme.palette.text.primary}
                size={"1rem"}
                className="position-icon"
              />
            </Typography>
          </TableCell>
          <TableCell data-testid={"contract-plan-status"}>
            <Chip
              size="small"
              sx={{
                fontWeight: "bold",
                ...contractPlanStatusChipStyleMap[status],
              }}
              data-testid={"contract-plan-status-chip"}
              label={contractPlanStatusToString[status]}
            />
          </TableCell>
          <TableCell data-testid={"contract-plan-type"}>
            {contractPlanTypeToString[planType]}
          </TableCell>
          <TableCell data-testid={"contract-plan-billing-frequency"}>
            {contractBillingFreqToString[billingFrequency]}
          </TableCell>
          <TableCell data-testid={"contract-plan-coupon-codes"}>
            {couponCodes.length > 0 ? couponCodes.join(", ") : "N/A"}
          </TableCell>
          <TableCell data-testid={"contract-plan-currency"}>
            {currency || "N/A"}
          </TableCell>
        </TableRow>
        <TableRow>
          <ContractPlanComponentsRecord
            contractPlanComponents={components}
            contractsTabColSpan={contractsTabColSpan}
            isExpanded={isPlanExpanded}
            currency={currency}
            priceOverrides={priceOverrides}
          />
        </TableRow>
      </>
    );
  };

  return (
    <>
      <TableCell
        style={{ paddingBottom: 0, paddingTop: 0 }}
        colSpan={contractsTabColSpan}
      >
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ margin: 1 }}>
            <Typography variant="body1" gutterBottom component="div">
              <b>Plans</b>
            </Typography>
            <Table size="small" aria-label="contract-plans-table">
              <TableHead>{displayContractPlanHeadings()}</TableHead>
              <TableBody data-testid={"contract-plan-records"}>
                {populateContractPlan()}
              </TableBody>
            </Table>
          </Box>
        </Collapse>
      </TableCell>
    </>
  );
};

export default ContractPlanRecord;
